import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import './seller-dashboard.css';
import { BottomNav, ColorRail, SELLER_NAV, TopBar } from '../../../commodity/components/caria';
import {
  activateListing,
  archiveListing,
  fetchMyListings,
  pauseListing,
  SellerListing,
  SellerListingStatus,
} from '../api/seller.api';
import { useAuthChrome } from '../../../core/identity-access/hooks/useAuthChrome';

/**
 * SellerDashboard (W-S-DASH-01 fidelity) — the private seller's "My Listings".
 * Wired to GET /vehicles/mine + the Epic 6 lifecycle endpoints
 * (activate/pause/archive). Mobile-first.
 */
const FALLBACK = [
  'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=900&h=600&fit=crop&q=85',
  'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=900&h=600&fit=crop&q=85',
  'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=900&h=600&fit=crop&q=85',
];
const usd = (n?: number) => (n != null ? `$${Math.round(n).toLocaleString('en-US')}` : '—');
const km = (n: number) => n.toLocaleString('en-US');

const STATUS_LABEL: Record<SellerListingStatus, string> = {
  DRAFT: 'Borrador',
  ACTIVE: 'Activo',
  PAUSED: 'Pausado',
  ARCHIVED: 'Archivado',
  SUSPENDED: 'Suspendido',
};

/** Readiness from the fields the list endpoint returns (VIN + price + tasación). */
const health = (l: SellerListing): number => {
  const checks = [l.vinVerified, l.listingPrice != null, l.suggestedPrice != null];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
};

export const SellerDashboard: React.FC = () => {
  const authChrome = useAuthChrome();
  const navigate = useNavigate();
  const [listings, setListings] = useState<SellerListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState<string | undefined>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      setListings(await fetchMyListings());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar tus listings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id: string, fn: (id: string) => Promise<unknown>) => {
    setBusy(id);
    setError(undefined);
    try {
      await fn(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'La acción no se pudo completar.');
    } finally {
      setBusy(undefined);
    }
  };

  const kpi = {
    total: listings.length,
    active: listings.filter((l) => l.status === 'ACTIVE').length,
    paused: listings.filter((l) => l.status === 'PAUSED').length,
    draft: listings.filter((l) => l.status === 'DRAFT').length,
  };

  return (
    <div className="cia-sell">
      <TopBar {...authChrome} nav={SELLER_NAV} />
      <ColorRail />

      <div className="cia-sell-head">
        <div>
          <div className="cia-sell-eye">Panel del vendedor</div>
          <h1 className="cia-sell-h1">Mis listings</h1>
        </div>
        <button
          className="cia-sell-btn cia-sell-btn--primary"
          data-testid="new-listing"
          onClick={() => navigate('/publish')}
        >
          + Publicar vehículo
        </button>
      </div>

      <div className="cia-sell-kpis">
        <div className="cia-sell-kpi">
          <div className="cia-sell-kpi-num">{kpi.total}</div>
          <div className="cia-sell-kpi-label">Total</div>
        </div>
        <div className="cia-sell-kpi">
          <div className="cia-sell-kpi-num">{kpi.active}</div>
          <div className="cia-sell-kpi-label">Activos</div>
        </div>
        <div className="cia-sell-kpi">
          <div className="cia-sell-kpi-num">{kpi.paused}</div>
          <div className="cia-sell-kpi-label">Pausados</div>
        </div>
        <div className="cia-sell-kpi">
          <div className="cia-sell-kpi-num">{kpi.draft}</div>
          <div className="cia-sell-kpi-label">Borradores</div>
        </div>
      </div>

      <div className="cia-sell-section-eye">Tus vehículos</div>

      {loading && <p className="cia-sell-state">Cargando…</p>}
      {!loading && error && (
        <p className="cia-sell-state" role="alert">
          {error}
        </p>
      )}
      {!loading && !error && listings.length === 0 && (
        <p className="cia-sell-state">Aún no tienes listings publicados.</p>
      )}

      <div className="cia-sell-list">
        {listings.map((l, i) => {
          const stClass = `cia-sell-st-${l.status.toLowerCase()}`;
          const pct = health(l);
          const disabled = busy === l.id;
          return (
            <article key={l.id} className="cia-sell-card">
              <div
                className="cia-sell-photo"
                style={{ backgroundImage: `url('${FALLBACK[i % FALLBACK.length]}')` }}
              >
                <span className={`cia-sell-status ${stClass}`}>
                  <span className="cia-sell-status-dot" />
                  {STATUS_LABEL[l.status]}
                </span>
              </div>
              <div className="cia-sell-info">
                <div className="cia-sell-title-row">
                  <span className="cia-sell-title">
                    {l.make} {l.model} {l.year}
                  </span>
                  <span className="cia-sell-price">{usd(l.listingPrice)}</span>
                </div>
                <div className="cia-sell-meta">
                  <span className="cia-sell-meta-mono">{km(l.mileage)}</span> km
                  {l.suggestedPrice != null && (
                    <>
                      <span>·</span>
                      <span>Tasador {usd(l.suggestedPrice)}</span>
                    </>
                  )}
                </div>
                <div className="cia-sell-health">
                  <div className="cia-sell-health-row">
                    <span className="cia-sell-health-label">Salud del listing</span>
                    <span className="cia-sell-health-pct">{pct}%</span>
                  </div>
                  <div className="cia-sell-health-bar">
                    <div className="cia-sell-health-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="cia-sell-actions">
                  {l.status === 'DRAFT' && (
                    <button
                      className="cia-sell-btn cia-sell-btn--primary"
                      onClick={() => navigate(`/vehicles/${l.id}/publish`)}
                    >
                      Continuar
                    </button>
                  )}
                  {l.status === 'PAUSED' && (
                    <button
                      className="cia-sell-btn cia-sell-btn--primary"
                      disabled={disabled}
                      onClick={() => act(l.id, activateListing)}
                    >
                      Activar
                    </button>
                  )}
                  {l.status === 'ACTIVE' && (
                    <button
                      className="cia-sell-btn"
                      disabled={disabled}
                      onClick={() => act(l.id, pauseListing)}
                    >
                      Pausar
                    </button>
                  )}
                  {(l.status === 'ACTIVE' || l.status === 'PAUSED') && (
                    <button
                      className="cia-sell-btn cia-sell-btn--danger"
                      disabled={disabled}
                      onClick={() => act(l.id, archiveListing)}
                    >
                      Archivar
                    </button>
                  )}
                  <button className="cia-sell-btn" onClick={() => navigate(`/vehicles/${l.id}`)}>
                    Ver
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <BottomNav />
    </div>
  );
};

export default SellerDashboard;
